import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

interface DocumentSection {
  id: string;
  title: string;
  description: string;
  path: string;
  pages: number;
}

const DevSecOpsDocumentation: React.FC = () => {
  const navigate = useNavigate();
  const [selectedSections, setSelectedSections] = useState<string[]>([
    'overview',
    'pipeline',
    'metrics',
    'teams',
    'transition',
    'deliverables',
    'quality'
  ]);
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);
  const [allComponentsByKey, setAllComponentsByKey] = useState<Record<string, DevSecOpsComponentDto>>({});

  const documentSections: DocumentSection[] = [
    {
      id: 'overview',
      title: 'Program Overview',
      description: 'ODOS IV opportunity snapshot, mission, and key capabilities',
      path: '/devsecops/overview',
      pages: 4
    },
    {
      id: 'pipeline',
      title: 'CI/CD Pipeline',
      description: 'Automated build, test, security, and deployment pipeline',
      path: '/devsecops/pipeline',
      pages: 3
    },
    {
      id: 'metrics',
      title: 'Metrics Dashboard',
      description: 'DORA metrics, quality indicators, and performance tracking',
      path: '/devsecops/metrics',
      pages: 3
    },
    {
      id: 'teams',
      title: 'Team Composition',
      description: 'DevSecOps, UXD/IxD, and Infrastructure team structure',
      path: '/devsecops/teams',
      pages: 3
    },
    {
      id: 'transition',
      title: 'Transition & Support',
      description: 'Transition-in, ongoing support, and transition-out plans',
      path: '/devsecops/transition',
      pages: 4
    },
    {
      id: 'deliverables',
      title: 'Deliverables & Schedules',
      description: 'Management artifacts, schedules, and acceptance criteria',
      path: '/devsecops/deliverables',
      pages: 3
    },
    {
      id: 'quality',
      title: 'Quality Assurance',
      description: 'QCP, performance requirements, and CI/CD quality gates',
      path: '/devsecops/quality',
      pages: 3
    }
  ];

  const handleBack = () => {
    navigate('/devsecops');
  };

  const proposalPackageMapping: Record<string, string[]> = {
    overview: [
      'overview-node-react-control-plane',
      'overview-automation-by-default-ci-cd',
      'overview-policy-as-code-guardrails',
      'overview-integrated-observability',
      'overview-multi-environment-deployment',
      'overview-section-508-compliance'
    ],
    pipeline: [
      'pipeline-sbom-generation',
      'pipeline-container-build-and-scan',
      'pipeline-kubernetes-deployments',
      'pipeline-security-gates',
      'pipeline-build-verification-tests',
      'pipeline-infra-as-code-validation',
      'pipeline-release-approvals-change-control'
    ],
    metrics: [
      'metrics-dora-overview',
      'metrics-slos-and-slis',
      'metrics-quality-and-coverage',
      'metrics-error-budget-policy',
      'metrics-customer-experience',
      'metrics-fedramp-ato-reporting'
    ],
    teams: [
      'teams-devsecops-squad-structure',
      'teams-uxd-and-accessibility-roles',
      'teams-sre-and-oncall-model'
    ],
    transition: [
      'transition-in-plan-overview',
      'transition-ongoing-support-model',
      'transition-out-plan',
      'transition-knowledge-transfer-playbook'
    ],
    deliverables: [
      'deliverables-management-artifacts',
      'deliverables-reporting-cadence',
      'deliverables-acceptance-criteria',
      'deliverables-security-compliance-packages'
    ],
    quality: [
      'quality-qcp-overview',
      'quality-testing-strategy',
      'quality-cicd-quality-gates',
      'quality-defect-management-rca'
    ]
  };

  const handleToggleSection = (sectionId: string) => {
    setSelectedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleSelectAll = () => {
    setSelectedSections(documentSections.map(s => s.id));
  };

  const handleDeselectAll = () => {
    setSelectedSections([]);
  };

  const handleGeneratePDF = () => {
    const htmlSections: string[] = [];

    documentSections
      .filter((section) => selectedSections.includes(section.id))
      .forEach((section) => {
        const componentKeys = proposalPackageMapping[section.id] || [];
        const sectionTitle = section.title;

        htmlSections.push(`<h1>${sectionTitle}</h1>`);

        componentKeys.forEach((key) => {
          const component = allComponentsByKey[key];
          if (!component) {
            return;
          }

          htmlSections.push(`<h2>${component.title}</h2>`);

          if (component.subtitle) {
            htmlSections.push(`<p><em>${component.subtitle}</em></p>`);
          }

          const text =
            typeof component.content?.text === 'string'
              ? component.content.text
              : typeof component.content?.html === 'string'
              ? component.content.html
              : '';

          if (text) {
            const safeText = text.replace(/\n/g, '<br />');
            htmlSections.push(`<p>${safeText}</p>`);
          }
        });

        htmlSections.push('<hr />');
      });

    const htmlContent = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charSet="utf-8" />
    <title>DevSecOps Proposal Package</title>
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 40px; color: #111827; }
      h1 { color: #003366; border-bottom: 2px solid #e5e7eb; padding-bottom: 4px; }
      h2 { color: #111827; margin-top: 16px; }
      em { color: #4b5563; }
      hr { margin: 24px 0; border: none; border-top: 1px solid #e5e7eb; }
    </style>
  </head>
  <body>
    ${htmlSections.join('\n')}
  </body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Unable to open print window. Please allow pop-ups for this site.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    // Give the new window a moment to render before printing
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleExportMarkdown = () => {
    const lines: string[] = [];

    documentSections
      .filter((section) => selectedSections.includes(section.id))
      .forEach((section) => {
        const sectionTitle = section.title;
        const componentKeys = proposalPackageMapping[section.id] || [];

        lines.push(`# ${sectionTitle}`);

        componentKeys.forEach((key) => {
          const component = allComponentsByKey[key];
          if (!component) {
            return;
          }

          lines.push('');
          lines.push(`## ${component.title}`);

          if (component.subtitle) {
            lines.push(component.subtitle);
            lines.push('');
          }

          const text =
            typeof component.content?.text === 'string'
              ? component.content.text
              : typeof component.content?.html === 'string'
              ? component.content.html
              : '';

          if (text) {
            lines.push(text);
            lines.push('');
          }
        });

        lines.push('');
      });

    const markdown = lines.join('\n');
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'devsecops-proposal-package.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totalPages = documentSections
    .filter(s => selectedSections.includes(s.id))
    .reduce((sum, s) => sum + s.pages, 0);

  const exportFormats = [
    {
      title: 'PDF Document',
      description: 'Professional PDF with USCIS branding and table of contents',
      icon: <PdfIcon />,
      action: handleGeneratePDF,
      disabled: selectedSections.length === 0
    },
    {
      title: 'Markdown Files',
      description: 'Individual markdown files for each section',
      icon: <DescriptionIcon />,
      action: handleExportMarkdown,
      disabled: selectedSections.length === 0
    }
  ];

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>('/devsecops/components');

        const data = response.data.data || [];
        const docs = data.filter((component) => component.page === 'documentation');
        setComponents(docs);

        const byKey: Record<string, DevSecOpsComponentDto> = {};
        data.forEach((component) => {
          byKey[component.key] = component;
        });
        setAllComponentsByKey(byKey);

        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load documentation components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (_err) {
        // unauthenticated users will see read-only cards
      }
    };

    loadUser();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Documentation & Export
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Generate Printable Documentation for DevSecOps Program
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="documentation" />

      {/* Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Export Documentation
          </Typography>
          <Typography variant="body1" paragraph>
            Select the sections you want to include in your documentation export. Generate a professional
            PDF document with USCIS branding, or export individual markdown files for version control.
          </Typography>
          <Alert severity="info">
            <strong>Selected:</strong> {selectedSections.length} sections ({totalPages} pages estimated)
          </Alert>
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        Documentation & Export Capabilities
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading documentation components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Section Selection */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  Select Sections
                </Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                    Select All
                  </Button>
                  <Button size="small" onClick={handleDeselectAll}>
                    Deselect All
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <FormGroup>
                {documentSections.map((section) => (
                  <Card
                    key={section.id}
                    sx={{
                      mb: 2,
                      border: selectedSections.includes(section.id) ? '2px solid #003366' : '1px solid #e0e0e0',
                      bgcolor: selectedSections.includes(section.id) ? '#f0f4f8' : 'white'
                    }}
                  >
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSections.includes(section.id)}
                            onChange={() => handleToggleSection(section.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="h6" fontWeight="bold">
                              {section.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {section.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Estimated pages: {section.pages}
                            </Typography>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Export Options */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Export Formats
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {exportFormats.map((format, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' }
                  }}
                  onClick={format.disabled ? undefined : format.action}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ color: 'primary.main', mr: 2 }}>
                        {format.icon}
                      </Box>
                      <Typography variant="h6" fontWeight="bold">
                        {format.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {format.description}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<DownloadIcon />}
                      disabled={format.disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        format.action();
                      }}
                    >
                      Export
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>

          {/* Export Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Export Summary
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sections Selected"
                    secondary={`${selectedSections.length} of ${documentSections.length}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Estimated Pages"
                    secondary={`${totalPages} pages`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Format"
                    secondary="PDF with TOC"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Branding"
                    secondary="USCIS Theme"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Use Case Link */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                Business Use Case
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                View the complete ODOS IV DevSecOps proposal use case demonstrating business value for USCIS OIT TDD and SCOPS.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<OpenInNewIcon />}
                onClick={() => navigate('/devsecops/usecase')}
              >
                View Use Case
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DevSecOpsDocumentation;
